
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MessageSquare, Shield, Layers, Grid, Clock } from "lucide-react";
import FloatingAI from "./FloatingAI";
import DigitalTerrain from "./DigitalTerrain";

const trustIndicators = [
  { icon: Shield, label: "Secure & Reliable" },
  { icon: Layers, label: "Scalable Architecture" },
  { icon: Grid, label: "Enterprise Ready" },
  { icon: Clock, label: "24/7 Support" },
];

const Hero = () => {
  return (
    <section
      className="relative isolate overflow-hidden pb-0 pt-24 sm:pt-28 lg:pt-28 flex flex-col justify-between"
      style={{ background: "#020617" }}
    >
      {/* Glow effects */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 20% 30%, rgba(0, 210, 255, 0.15), transparent 45%), " +
            "radial-gradient(circle at 80% 20%, rgba(0, 210, 255, 0.12), transparent 40%)",
        }}
      />

      {/* Grid overlay */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0, 210, 255, 0.03) 1px, transparent 1px), " +
            "linear-gradient(90deg, rgba(0, 210, 255, 0.03) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          opacity: 0.6,
        }}
      />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full pt-2 sm:pt-3 lg:pt-5 grid gap-8 lg:grid-cols-2 lg:items-start xl:gap-12">
        {/* Left Side */}
        <div className="relative z-20 max-w-[38rem] text-left">
          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.75 }}
            className="text-2xl sm:text-3xl lg:text-4xl xl:text-[2.75rem] font-bold leading-[1.15] text-white"
            style={{ fontFamily: '"Space Grotesk", sans-serif' }}
          >
            Automate Everything
            <br />
            With{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #7C4DFF, #00D2FF)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                filter: "drop-shadow(0 0 20px rgba(0, 210, 255, 0.35))",
              }}
            >
              Generative AI
            </span>
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl"
          >
            Build AI Chatbots, AI Agents, Business Process Automation, and Predictive AI Solutions—managed through one powerful AI Command Center to accelerate business growth.
          </motion.p>

          {/* Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <Link
              to="/services"
              className="relative inline-flex items-center gap-2 px-7 py-4 rounded-xl font-bold text-white text-sm shadow-[0_0_30px_rgba(0,210,255,0.4)] transition-all duration-300 hover:scale-105 hover:shadow-[0_0_45px_rgba(0,210,255,0.6)] group overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #7C4DFF, #00D2FF)",
              }}
            >
              <span className="relative z-10 flex items-center gap-2">
                Explore Solutions
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
              <span className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </Link>

            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-xl font-semibold text-white text-sm bg-transparent border border-white/10 transition-all duration-300 hover:bg-white/5 hover:border-white/20 hover:scale-105"
            >
              Talk to AI Expert
              <MessageSquare className="h-4 w-4" />
            </Link>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.6 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            {trustIndicators.map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-2 text-xs font-semibold text-slate-400"
              >
                <item.icon className="h-4 w-4" style={{ color: "#00D2FF" }} />
                <span>{item.label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right Side */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
          className="w-full flex justify-center items-center relative z-10 scale-75 xs:scale-85 sm:scale-95 lg:scale-100 min-h-[380px] sm:min-h-[460px] overflow-hidden lg:overflow-visible -mt-3 sm:-mt-5 lg:-mt-6"
        >
          <FloatingAI />
        </motion.div>
      </div>

      {/* Terrain Wave */}
      <div className="relative w-full h-[200px] mt-8 lg:mt-0">
        <DigitalTerrain />
      </div>
    </section>
  );
};

export default Hero;
